/**
 * Refinitiv (Eikon / Workspace Excel) compatibility layer.
 *
 * Pure helpers — no Office or network calls — that let legacy sheets written
 * against the Refinitiv add-in (`=TR(...)`, `=RHistory(...)`) run unchanged
 * as `=EAV.TR(...)` / `=EAV.RHISTORY(...)`:
 *
 * - instruments / fields may be a cell range, not just a string;
 * - Excel date serials (what `"START:"&A1` produces) become ISO dates;
 * - display parameters (TR `CH= RH= TRANSPOSE= NULL=`, RHistory
 *   `CH: SORT:`) are applied client-side and never reach DataGrid;
 * - legacy RHistory fields (`.Timestamp`, `.Close`, `.High`…) are mapped
 *   to the real-time FIDs get_history understands;
 * - output follows Refinitiv's layout: bare values, no header row unless one
 *   is asked for — so `=EAV.TR(A1,"CF_BID")/100` stays a scalar.
 */

import type { TableResult } from "./api";
import { invalidDisplayParam, invalidRefreshParam, invalidRequestParam, invalidValueFor, LSEG_TEXT, noValueForParam } from "./lsegText";

// --- Arguments --------------------------------------------------------------

/** A custom-function argument: a scalar, or a 2-D range when declared any[][]. */
export type Arg = any;

function cellText(v: any): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function cellsOf(arg: Arg): any[] {
  return Array.isArray(arg) ? (arg as any[][]).reduce((a: any[], r) => a.concat(r), []) : [arg];
}

/**
 * Flatten an instruments argument into one entry per RIC, row-major.
 *
 * A single string ("VOD.L,AAPL.O") splits on `,`/`;`. A range keeps one
 * entry per cell — blank cells included as "" — so the output stays aligned
 * row-for-row with the input range (what `X4 = W4` style sheets rely on).
 * Trailing blanks are trimmed so an oversized range like C3:C1995 does not
 * spill a thousand empty rows.
 */
/**
 * An identifier LSEG's TR does not even look up: "The invalid identifier
 * ignored." on its row (goldens z_tr_invalid_identifier_*, tr_rh_timestamp_dest:
 * "-"). Captured for "-" only; any identifier without a letter or a digit is
 * read the same way [A].
 */
export const isInvalidIdentifier = (ric: string): boolean => !!ric && !/[A-Za-z0-9]/.test(ric);

export function flattenInstruments(arg: Arg): string[] {
  const cells = cellsOf(arg);
  const isRange = cells.length > 1;
  const out: string[] = [];
  for (const c of cells) {
    const parts = cellText(c).split(/[,;]/).map((p) => p.trim()).filter(Boolean);
    if (parts.length) out.push(...parts);
    else if (isRange) out.push("");
  }
  while (out.length && out[out.length - 1] === "") out.pop();
  return out;
}

/**
 * Flatten a fields argument. Splits on `;` and on `,` outside parentheses so
 * `TR.CLOSEPRICE(SDate=0,EDate=-5)` stays one field. Blanks are dropped.
 * `keepSemicolonInParens`: a `;` inside parentheses does not split either, so
 * `TR.Revenue(Period=FY0;Scale=6)` stays one field (TRAggregate's single item;
 * TR itself splits there, and keeps doing so by default). `splitOnComma: false`:
 * a `,` never splits (only `;` does).
 */
export function flattenFields(
  arg: Arg,
  { keepSemicolonInParens = false, splitOnComma = true }: { keepSemicolonInParens?: boolean; splitOnComma?: boolean } = {}
): string[] {
  const out: string[] = [];
  for (const c of cellsOf(arg)) {
    let depth = 0;
    let cur = "";
    for (const ch of cellText(c)) {
      if (ch === "(") depth++;
      else if (ch === ")") depth = Math.max(0, depth - 1);
      if ((ch === ";" && (!keepSemicolonInParens || depth === 0)) || (splitOnComma && ch === "," && depth === 0)) {
        if (cur.trim()) out.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    if (cur.trim()) out.push(cur.trim());
  }
  return out;
}

/** Scalar argument → trimmed string ("" when omitted). Ranges use their top-left cell. */
export function scalarText(arg: Arg): string {
  if (Array.isArray(arg)) return cellText(arg[0] && arg[0][0]);
  return cellText(arg);
}

// --- Dates ------------------------------------------------------------------

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const DAY_MS = 86400000;

/** Excel date serial (e.g. 46307) → "2026-10-12". */
export function serialToIso(serial: number): string {
  return new Date(EXCEL_EPOCH_MS + Math.floor(serial) * DAY_MS).toISOString().slice(0, 10);
}

/** True for a number/string that can only sensibly be an Excel date serial (1954–2173). */
export function isDateSerial(v: any): boolean {
  const s = cellText(v);
  if (!/^\d{5}(\.\d+)?$/.test(s)) return false;
  const n = Number(s);
  return n >= 20000 && n <= 100000;
}

/** Normalize a date argument: serial → ISO, anything else (0D, -1Y, 2024-01-01) unchanged. */
export function normalizeDate(v: any): string {
  return isDateSerial(v) ? serialToIso(Number(v)) : cellText(v);
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/** ISO date/datetime string → Excel serial, so the cell is a real date. Non-dates pass through. */
export function isoToSerial(v: any): any {
  if (typeof v !== "string") return v;
  const m = ISO_DATE_RE.exec(v.trim());
  if (!m) return v;
  const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
  return (ms - EXCEL_EPOCH_MS) / DAY_MS;
}

/** The PC's UTC offset in minutes (west positive, as Date.getTimezoneOffset) at the UTC instant `ms`. */
const pcOffsetMinutes = (ms: number): number => new Date(ms).getTimezoneOffset();

/**
 * An ISO date-time → the Excel serial of that instant as the PC's clock shows
 * it (intraday bars and ticks, goldens x_rh_intraday_aapl_tz, w_rh_tick_value_nbrows). The hub's timestamps are
 * UTC: no offset means UTC; a written offset (Z, +02:00) is honoured. The PC
 * offset is the one in force at that instant, so DST is right per value.
 * Anything but a date-time (a plain date, a non-date) is left to isoToSerial.
 */
export function isoToLocalSerial(
  v: any,
  offsetMinutes: (utcMs: number) => number = pcOffsetMinutes,
  /** Added to a date-time's instant before the PC offset is applied (an intraday bar's start → its end). */
  shiftMinutes = 0
): any {
  if (typeof v !== "string") return v;
  const m = ISO_DATE_RE.exec(v.trim());
  if (!m || m[4] === undefined) return isoToSerial(v);
  let ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  if (m[7] && m[7] !== "Z") {
    const sign = m[7][0] === "-" ? -1 : 1;
    const digits = m[7].slice(1).replace(":", "");
    ms -= sign * (+digits.slice(0, 2) * 60 + +digits.slice(2)) * 60000;
  }
  ms += shiftMinutes * 60000;
  return (ms - offsetMinutes(ms) * 60000 - EXCEL_EPOCH_MS) / DAY_MS;
}

/**
 * A date cell of a laid-out grid: an Excel serial the writer shows with the
 * short-date format (spec §5.1). Only layoutTrCells / layoutHistoryCells
 * produce it, where they convert an ISO date; unmarkDates turns it back into
 * the plain serial.
 */
export class DateSerial {
  /** `datetime`: a timestamp with a time of day (tick data), shown m/d/yyyy h:mm. */
  constructor(readonly serial: number, readonly datetime = false) {}
}

/**
 * A real Excel #N/A error in a laid-out grid: an RHistory gap under NULL:NA
 * (golden w_rh_null_na_blanks). The one place LSEG writes an error, not text.
 */
export class NaError {}
const NA_ERROR = new NaError();

/**
 * A time-of-day cell of a laid-out grid (CF_TIME): a fraction of a day the
 * writer shows as h:mm:ss AM/PM (goldens tr_cf_time_alone, tr_cf_meta_*).
 * unmarkDates turns it back into the plain fraction.
 */
export class TimeSerial {
  constructor(readonly serial: number) {}
}

const UTC_TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

/**
 * A UTC time of day ("07:52:33", the QUOTIM FID) → the PC's local time of day
 * as a fraction of a day in [0, 1): the local clock reading, never a date,
 * wrapped across midnight (golden tr_cf_time_alone: 0.3698263888888889 is
 * 8:52:33 for a 07:52:33 UTC quote in Paris).
 *
 * The quote is the latest occurrence of that UTC time not after `nowMs`, so
 * the offset (DST included) is the one in force at that instant. Both the
 * zone (`offsetMinutes`, west positive like Date.getTimezoneOffset) and the
 * clock are injectable. Anything that is not a time of day is returned as is.
 */
export function utcTimeToLocalFraction(
  v: any,
  offsetMinutes: (utcMs: number) => number = pcOffsetMinutes,
  nowMs: number = Date.now()
): any {
  if (typeof v !== "string") return v;
  const m = UTC_TIME_RE.exec(v.trim());
  if (!m || +m[1] > 23 || +m[2] > 59 || +(m[3] || 0) > 59) return v;
  const secs = +m[1] * 3600 + +m[2] * 60 + +(m[3] || 0);
  let quoteMs = nowMs - (((nowMs % DAY_MS) + DAY_MS) % DAY_MS) + secs * 1000;
  if (quoteMs > nowMs) quoteMs -= DAY_MS;
  const localSecs = secs - offsetMinutes(quoteMs) * 60;
  return (((localSecs % 86400) + 86400) % 86400) / 86400;
}

/** isoToSerial (isoToLocalSerial, shifted by `shiftMinutes`, when `local`), marking a converted ISO date as a DateSerial. */
function dateCell(v: any, datetime = false, local = false, shiftMinutes = 0): any {
  const s = local ? isoToLocalSerial(v, undefined, shiftMinutes) : isoToSerial(v);
  return typeof v === "string" && typeof s === "number" ? new DateSerial(s, datetime) : s;
}

/** A grid with its DateSerial / TimeSerial cells replaced by their serials (and #N/A errors by their text). */
export function unmarkDates(grid: any[][]): any[][] {
  return grid.map((row) =>
    row.map((v) => (v instanceof DateSerial || v instanceof TimeSerial ? v.serial : v instanceof NaError ? "#N/A" : v))
  );
}

// --- Parameter strings ------------------------------------------------------

export interface KV {
  key: string;
  value: string;
}

/**
 * Parse `"SDate=2024-01-01 Curn=EUR"` (sep "=") or
 * `"START:2024-01-01 END:0D INTERVAL:1D"` (sep ":", `=` also tolerated) into
 * ordered pairs. Pairs are separated by whitespace or commas; values may be
 * "quoted". A key written with no value (`NULL=`, `NULL= CH=Fd`) gets ""; the
 * next pair is never read as its value. With sep "=" (TR), a bare word is a
 * key with no value too (`EXCLUDEVA`, golden v_tr_excludeva_no_rh), but a
 * word after a value and a comma continues that value (`RH=In,Date`, golden
 * w_tr_rh_in_comma_date).
 */
export function parseKv(s: string, sep: "=" | ":"): KV[] {
  const re =
    sep === "="
      ? /(\w+)(?:\s*=(?:\s*"([^"]*)"|\s*(?!\w+\s*=)([^\s,]+(?:,(?!\s*\w+\s*=)[^\s,]+)*))?)?/g
      : /(\w+)\s*[:=](?:\s*"([^"]*)"|\s*(?!\w+\s*[:=])([^\s,]+))?/g;
  const out: KV[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(s || ""))) out.push({ key: m[1], value: m[2] ?? m[3] ?? "" });
  return out;
}

function kvToString(pairs: KV[], sep: "=" | ":"): string {
  return pairs.map((p) => `${p.key}${sep}${/\s/.test(p.value) ? `"${p.value}"` : p.value}`).join(" ");
}

/** True when a value carries `word:`/`word=` pairs, i.e. is a parameter blob, not a plain value. */
export function looksLikeParams(v: any): boolean {
  return typeof v === "string" && /\w\s*[:=]/.test(v);
}

/**
 * LSEG's `Arg 1..20`: `#1`..`#20` inside a string argument (or each string
 * cell of a range) become the matching argument's value; a date serial is
 * sent as its ISO date (golden tr_hash_arg_substitution: `SDate=#1` with a
 * date in D2). A `#n` whose argument is missing stays as typed.
 */
export function substituteArgs(arg: Arg, args: any[]): Arg {
  const sub = (v: any) =>
    typeof v === "string"
      ? v.replace(/#(\d{1,2})/g, (m, d) => {
          const a = args[Number(d) - 1];
          return Number(d) >= 1 && a !== undefined && a !== null ? normalizeDate(a) : m;
        })
      : v;
  return Array.isArray(arg) ? (arg as any[][]).map((row) => row.map(sub)) : sub(arg);
}

function pad(m: any[][]): any[][] {
  const w = m.reduce((a, r) => Math.max(a, r.length), 0);
  return m.map((r) => (r.length < w ? r.concat(new Array(w - r.length).fill("")) : r));
}

function transpose(m: any[][]): any[][] {
  const w = m.reduce((a, r) => Math.max(a, r.length), 0);
  const out: any[][] = [];
  for (let c = 0; c < w; c++) out.push(m.map((r) => (c < r.length ? r[c] : "")));
  return out;
}

// --- TR ---------------------------------------------------------------------

const TR_DATE_KEYS = new Set(["SDATE", "EDATE"]);
const TR_DISPLAY_KEYS = new Set(["CH", "RH", "TRANSPOSE", "NULL"]);

export interface TrDisplay {
  /** Column headers requested (CH=…). */
  columnHeaders: boolean;
  /** Row-header columns requested (RH=…), in order. */
  rowHeaders: ("instrument" | "date")[];
  /**
   * RH=…;EXCLUDEVA: the row-header columns only, no value columns; with CH
   * they are headed like data columns (golden opt_tr_excludeva: "Instrument").
   */
  excludeValues?: boolean;
  transpose: boolean;
  /** Replacement for null cells (NULL=…); undefined → "". */
  nullValue?: string;
}

export interface TrRequest {
  /** Parameter string forwarded to DataGrid (display keys stripped, serials → ISO). */
  params: string;
  display: TrDisplay;
  /** LSEG's text for an option it rejects before fetching (the first one), e.g. `NULL=` with no value. */
  error?: string;
}

/**
 * Split a TR parameter string into the DataGrid part and the display part.
 * Every key but the display ones (CH, RH, TRANSPOSE, NULL) goes to DataGrid
 * as typed, unknown keys included, as LSEG forwards them to ADC (spec §6.1):
 * SDate / EDate (serials → ISO, relative values as typed), Frq, Period,
 * Curn, Scale, RollPeriods, AlignType… A key with no value is LSEG's
 * "No value provided for parameter <KEY>" (golden opt_tr_null_blank).
 */
export function parseTrParams(raw: string): TrRequest {
  const display: TrDisplay = { columnHeaders: false, rowHeaders: [], transpose: false };
  const keep: KV[] = [];
  let error: string | undefined;
  for (const p of parseKv(raw, "=")) {
    const k = p.key.toUpperCase();
    if (p.value === "") {
      error = error || noValueForParam(p.key);
    } else if (!TR_DISPLAY_KEYS.has(k)) {
      keep.push(TR_DATE_KEYS.has(k) ? { key: p.key, value: normalizeDate(p.value) } : p);
    } else if (k === "CH") {
      display.columnHeaders = true;
    } else if (k === "RH") {
      for (const t of p.value.split(/[;,]/).map((x) => x.trim().toUpperCase())) {
        if (t === "IN" || t === "INSTRUMENT") display.rowHeaders.push("instrument");
        else if (t === "DATE" || t === "TIMESTAMP" || t === "CALCDATE") display.rowHeaders.push("date");
        else if (t === "EXCLUDEVA") display.excludeValues = true;
      }
    } else if (k === "TRANSPOSE") {
      display.transpose = /^(Y|YES|TRUE|1)$/i.test(p.value);
    } else {
      display.nullValue = /^BLANK$/i.test(p.value) ? "" : p.value;
    }
  }
  return { params: kvToString(keep, "="), display, error };
}

/** Prefix marking a source read off the live stream (RtGet) rather than DataGrid. */
const RT = "RT:";

// Eikon composite fields (CF_*) don't exist on the LSEG data platform —
// DataGrid silently drops them. Map each to the real-time FIDs it was built
// from; the first non-empty one wins. Contributed bond quotes (XS…=CGSP)
// leave BID/ASK empty and price in PRIMACT_1/SEC_ACT_1.
//
// Realtime FIDs are read off the live path (RtGet), never DataGrid: RtGet
// results carry the backend snapshot's `unresolved` list, the evidence
// LSEG's realtime semantics need (a known RIC with no value is 0, an unknown
// one "The record could not be found (Error code: 0)", golden batch6).
// DataGrid has no such evidence for realtime FIDs.
//
// CF_TIME / CF_TICK / CF_SOURCE / CF_EXCHNG / CF_CURR are the FIDs probed
// live on 2026-10-26 (goldens batch26 tr_cf_meta_*): see the entries below.
const CF_FIELDS: Record<string, string[]> = {
  // Dividend yield as the realtime record holds it, rounded (golden 2.932 /
  // 0.3101); TR.DividendYield is full precision (2.932233) and differs.
  CF_YIELD: [`${RT}YIELD`],
  // Eikon's CF_NAME is the realtime display name ("ACCOR/d" for ACCP.PA,
  // golden tr_cf_close_name_accp), not TR.CommonName ("Accor SA"). The
  // interface fills realtime FIDs missing from the stream from the hub
  // snapshot, so DSPLY_NAME comes back through RtGet.
  CF_NAME: [`${RT}DSPLY_NAME`],
  CF_BID: [`${RT}BID`, `${RT}PRIMACT_1`],
  CF_ASK: [`${RT}ASK`, `${RT}SEC_ACT_1`],
  // Last trade: DataGrid has none, so read TRDPRC_1 off the live stream
  // (RtGet; delayed where not entitled), else the previous close — what
  // Eikon's CF_LAST shows before the first trade of the day. A quote-only
  // record (no TRDPRC_1 at all) shows the bid: CF_LAST_IF_NO_TRADES.
  CF_LAST: [`${RT}TRDPRC_1`, "TR.PriceClose"],
  // Annual dividend: a real FID, read off the live path only (the interface
  // fills it from the hub snapshot for delayed RICs). No DataGrid source.
  DIVIDEND: [`${RT}DIVIDEND`],
  // Previous close and trade date: realtime FIDs, live path (golden
  // tr_cf_close_name_accp). TRADE_DATE arrives as an ISO date and layoutTr
  // turns it into an Excel date serial; Office.js custom functions cannot
  // set the cell's m/d/yyyy number format, so the cell shows the serial
  // until the user formats it as a date.
  CF_CLOSE: [`${RT}HST_CLOSE`],
  CF_HIGH: [`${RT}HIGH_1`],
  CF_LOW: [`${RT}LOW_1`],
  CF_VOLUME: [`${RT}ACVOL_1`],
  CF_NETCHNG: [`${RT}NETCHNG_1`],
  CF_DATE: [`${RT}TRADE_DATE`],
  // QUOTIM is a UTC time of day; LSEG shows it in the PC's local time as a
  // fraction of a day (layoutTr: utcTimeToLocalFraction, marked TimeSerial).
  CF_TIME: [`${RT}QUOTIM`],
  // The last trade's tick arrow for equities, the bid's for quote-only FX.
  CF_TICK: [`${RT}PRCTCK_1`, `${RT}BID_TICK_1`],
  // The contributor (EUR= "BARCLAYS    ", padded as LSEG shows it), else the
  // exchange id [A].
  CF_SOURCE: [`${RT}CTBTR_1`, `${RT}RDN_EXCHID`],
  CF_EXCHNG: [`${RT}RDN_EXCHD2`],
  CF_CURR: [`${RT}CURRENCY`],
  CF_CURRENCY: [`${RT}CURRENCY`],
};

// How an output's value is shaped once read (layoutTr).
const CF_KINDS: Record<string, "time" | "tick"> = { CF_TIME: "time", CF_TICK: "tick" };

// CF_LAST of an instrument whose record has no TRDPRC_1 (quote-only: FX,
// crypto) is its BID: BTC= 80550.84, EUR= 1.1202 (goldens y_tr_cf_last_btc /
// _eur). A TRDPRC_1 present but blank keeps the TR.PriceClose fallback.
const CF_LAST_IF_NO_TRADES = { fid: "TRDPRC_1", sources: [`${RT}BID`] };

// CH=Fd headers LSEG shows for realtime fields (goldens y_tr_hdr_1..3,
// y_tr_hdr_rhin). Any other realtime field echoes its code [A].
const REALTIME_HEADERS: Record<string, string> = {
  CF_LAST: "Last", CF_BID: "Bid", CF_ASK: "Ask", CF_CLOSE: "Close", CF_HIGH: "High", CF_LOW: "Low",
  CF_VOLUME: "Volume", CF_NETCHNG: "Net Chng", CF_YIELD: "Yield", CF_OPEN: "CF OPEN",
  CF_DATE: "Date", CF_TIME: "Time", CF_NAME: "Name", CF_EXCHNG: "Exchange", CF_CURR: "Currency",
  CF_TICK: "Tick", CF_SOURCE: "Source",
  BID: "Bid", ASK: "Ask", TRDPRC_1: "Last", HST_CLOSE: "Close", OPEN_PRC: "Open", HIGH_1: "High",
  LOW_1: "Low", ACVOL_1: "Volume", NETCHNG_1: "Net.Chng", PCTCHNG: "Pct.Chng", DSPLY_NAME: "Indicator Name",
  YIELD: "Yield",
};

// CF_OPEN is no composite in LSEG's add-in: its header is "CF OPEN" and its
// value a real #N/A, where OPEN_PRC has one (golden y_tr_hdr_1 H3 vs y_tr_hdr_3 F3).
const NOT_AVAILABLE_FIELDS = new Set(["CF_OPEN"]);

const isTrField = (f: string) => /^TR\./i.test(f);
const isRealtime = (f: string) => f.startsWith(RT);

export interface TrOutput {
  label: string | null;
  /** Fields that can fill this column: first non-empty wins. */
  sources: string[];
  /**
   * A realtime FID typed as-is (BID, TRDPRC_1): when the instrument's record
   * does not have it at all, the cell is a real #N/A (golden y_tr_cf_last_btc
   * TRDPRC_1). Present but blank keeps the realtime rule (0).
   */
  bareFid?: boolean;
  /** The sources read instead when the instrument's record has no `fid` at all. */
  ifAbsent?: { fid: string; sources: string[] };
  /** CH=Fd header LSEG shows for this field, when it is not the field as typed (REALTIME_HEADERS). */
  header?: string;
  /** Always a real #N/A, nothing fetched (CF_OPEN, golden y_tr_hdr_1). */
  notAvailable?: boolean;
  /**
   * How the value is shaped: `time` a UTC time of day shown as the PC's local
   * time (CF_TIME); `tick` a tick arrow behind a space, blank while the record
   * has none (CF_TICK).
   */
  kind?: "time" | "tick";
}

export interface TrPlan {
  /** Fields sent to DataGrid (date field first when dateColumn). */
  request: string[];
  /** Real-time FIDs read off the live stream (RtGet), merged in as `RT:<FID>` columns. */
  realtime: string[];
  dateColumn: boolean;
  /** One per requested field. */
  outputs: TrOutput[];
}

/**
 * Plan a TR request: expand CF_* fields, and when a date row header is
 * wanted, request the first TR.* field's `.date` property up front.
 */
export function planTr(fields: string[], display: TrDisplay): TrPlan {
  const outputs: TrOutput[] = fields.map((f): TrOutput => {
    const key = f.toUpperCase();
    const headed = (o: TrOutput): TrOutput => {
      const header = REALTIME_HEADERS[isRealtime(key) ? key.slice(RT.length) : key];
      return header ? { ...o, header } : o;
    };
    if (NOT_AVAILABLE_FIELDS.has(key)) return headed({ label: f, sources: [], notAvailable: true });
    const cf = CF_FIELDS[key];
    if (cf) {
      const kind = CF_KINDS[key];
      return headed(
        key === "CF_LAST"
          ? { label: f, sources: cf, ifAbsent: CF_LAST_IF_NO_TRADES }
          : { label: f, sources: cf, ...(kind ? { kind } : {}) }
      );
    }
    if (isTrField(f)) return { label: null, sources: [f] };
    // Any other field (BID, PRIMACT_1, an unmapped CF_*…) is a realtime FID:
    // the live path, labelled as typed for CH= unless LSEG's header is known.
    // An unmapped CF_* is no FID: no #N/A rule for it.
    const bareFid = !/^(RT:)?CF_/i.test(f);
    return headed({ label: f, sources: [isRealtime(f) ? f : RT + f], ...(bareFid ? { bareFid } : {}) });
  });
  const request: string[] = [];
  const realtime: string[] = [];
  const firstTr = fields.find(isTrField);
  const dateColumn = display.rowHeaders.includes("date") && !!firstTr;
  if (dateColumn) request.push(`${firstTr}.date`);
  const add = (list: string[], f: string) => {
    if (!list.some((r) => r.toUpperCase() === f.toUpperCase())) list.push(f);
  };
  for (const o of outputs) {
    for (const s of o.sources.concat(o.ifAbsent ? o.ifAbsent.sources : [])) {
      isRealtime(s) ? add(realtime, s.slice(RT.length)) : add(request, s);
    }
  }
  return { request, realtime, dateColumn, outputs };
}

/**
 * Merge an instrument's live-stream row (RtGet, Instrument column removed)
 * into its DataGrid rows as `RT:<FID>` columns, so layoutTr can read both.
 */
export function mergeRealtime(grid: TableResult | null, live: TableResult | null): TableResult | null {
  if (!live || !live.data.length) return grid;
  const liveCols = live.columns.map((c) => RT + String(c));
  const liveRow = live.data[0];
  if (!grid || !grid.data.length) return { columns: liveCols, data: [liveRow] };
  return { columns: [...grid.columns, ...liveCols], data: grid.data.map((r) => [...r, ...liveRow]) };
}

/**
 * Map each request field to its response column. Real-time FIDs come back
 * under their own name; TR.* fields come back under display names ("Close
 * Price"), so they take the remaining columns in order. Missing → absent.
 */
function resolveColumns(columns: string[], request: string[]): Map<string, number> {
  // Real-time sources are matched by their merged `RT:<FID>` column name.
  request = request.concat(columns.filter((c) => isRealtime(String(c))).map(String));
  const out = new Map<string, number>();
  const claimed = new Set<number>();
  for (const f of request) {
    if (isTrField(f)) continue;
    const i = columns.findIndex((c, j) => !claimed.has(j) && String(c).toUpperCase() === f.toUpperCase());
    if (i >= 0) {
      out.set(f.toUpperCase(), i);
      claimed.add(i);
    }
  }
  let next = 0;
  for (const f of request) {
    if (!isTrField(f)) continue;
    while (next < columns.length && claimed.has(next)) next++;
    if (next < columns.length) {
      out.set(f.toUpperCase(), next);
      claimed.add(next++);
    }
  }
  return out;
}

/** The response column names an output reads from (resolved like layoutTr does). */
function outputHeaders(columns: string[], idx: Map<string, number>, o: TrOutput): string[] {
  return o.sources
    .map((s) => idx.get(s.toUpperCase()))
    .filter((i): i is number => i !== undefined)
    .map((i) => String(columns[i]));
}

/** outputHeaders for a DataGrid response and plan, for callers outside layoutTr. */
export function trOutputHeaders(columns: string[], plan: TrPlan, o: TrOutput): string[] {
  return outputHeaders(columns, resolveColumns(columns, plan.request), o);
}

/** An output column filled (at least in part) from DataGrid rather than the live stream. */
export const isDataGridOutput = (o: TrOutput) => o.sources.some((s) => !isRealtime(s));

/** The field of an output as the formula typed it (CF_* label, else the field itself). */
export const typedField = (o: TrOutput) => o.label ?? o.sources[0];

/** Per-cell rule for layoutTr's DataGrid columns. */
export interface TrCellRule {
  /**
   * Text for an EMPTY DataGrid cell of `ric` that LSEG reported an error for,
   * or null. `headers` are the response column names the output was read
   * from, so an error naming the header ("Close Price") matches too.
   */
  error?: (ric: string, output: TrOutput, headers: string[]) => string | null;
  /**
   * Shown for a null DataGrid value of a field typed as-is in `ric`'s rows
   * ("NULL"), or null to keep the blank (no per-cell evidence from the hub).
   */
  nullText?: (ric: string) => string | null;
  /**
   * Value of an EMPTY realtime-only cell (every source an `RT:` FID) of
   * `ric`, from the live result's `unresolved` evidence: LSEG's
   * record-not-found text for an unknown RIC, 0 for a known one, or null to
   * keep the blank (no evidence).
   */
  realtime?: (ric: string) => string | number | null;
  /** Whether `ric`'s realtime record lacks FID `fid` altogether (live evidence `absent`). */
  absent?: (ric: string, fid: string) => boolean;
  /** The PC's zone and clock for CF_TIME (default: the machine's own); injectable for tests. */
  clock?: { now: () => number; offsetMinutes: (utcMs: number) => number };
}

/**
 * Lay out TR results Refinitiv-style: one block of rows per input
 * instrument, in input order (blank input or no data → blank row), optional
 * row-header columns and header row, no "Instrument" column unless RH=IN.
 * `byRic` returns the instrument's rows with the Instrument column removed.
 * `rowError` returns the field cells of an instrument whose data could not
 * be collected, one per requested field (null → normal layout).
 */
export function layoutTr(
  instruments: string[],
  byRic: (ric: string) => TableResult | null,
  plan: TrPlan,
  display: TrDisplay,
  rowError?: (ric: string) => any[] | null,
  cells: TrCellRule = {}
): any[][] {
  return unmarkDates(layoutTrCells(instruments, byRic, plan, display, rowError, cells));
}

/** layoutTr with its date cells (RH=Date column, ISO date values) as DateSerial. */
export function layoutTrCells(
  instruments: string[],
  byRic: (ric: string) => TableResult | null,
  plan: TrPlan,
  display: TrDisplay,
  rowError?: (ric: string) => any[] | null,
  cells: TrCellRule = {}
): any[][] {
  const nul = display.nullValue !== undefined ? display.nullValue : "";
  // A null DataGrid cell of a field typed as-is shows LSEG's "NULL" unless
  // the formula set NULL= itself. CF_* composites and RT: columns keep "".
  const nullCell = (ric: string) => {
    if (display.nullValue !== undefined || !cells.nullText) return nul;
    const t = cells.nullText(ric);
    return t === null ? nul : t;
  };
  const empty = (v: any) => v === null || v === undefined || v === "";
  const body: any[][] = [];
  let labels: string[] | null = null;

  for (const ric of instruments) {
    const err = ric && rowError ? rowError(ric) : null;
    if (err !== null) {
      const row: any[] = display.rowHeaders.map((h) => (h === "instrument" ? ric : ""));
      plan.outputs.forEach((_, i) => row.push(i < err.length ? err[i] : ""));
      body.push(row);
      continue;
    }
    const res = ric ? byRic(ric) : null;
    if (!res || res.data.length === 0) {
      // A blank input row stays blank and keeps its place (golden tr_range_blank_middle).
      const row: any[] = display.rowHeaders.map((h) => (h === "instrument" ? ric : nul));
      // A never-available field is #N/A on an instrument's row, even with nothing fetched.
      plan.outputs.forEach((o) => row.push(ric && o.notAvailable ? NA_ERROR : nul));
      body.push(row);
      continue;
    }
    const idx = resolveColumns(res.columns, plan.request);
    const at = (r: any[], f: string) => {
      const i = idx.get(f.toUpperCase());
      return i === undefined ? null : r[i];
    };
    if (!labels) {
      labels = plan.outputs.map((o) => {
        if (o.header) return o.header;
        const i = o.sources.length ? idx.get(o.sources[0].toUpperCase()) : undefined;
        return o.label || (i !== undefined ? String(res.columns[i]) : o.sources[0]);
      });
    }
    for (const r of res.data) {
      const row: any[] = display.rowHeaders.map((h) => {
        if (h === "instrument") return ric;
        const d = plan.dateColumn ? at(r, plan.request[0]) : null;
        return empty(d) ? nul : dateCell(d);
      });
      for (const o of plan.outputs) {
        if (o.notAvailable) {
          row.push(NA_ERROR);
          continue;
        }
        const isAbsent = (fid: string) => !!cells.absent && cells.absent(ric, fid);
        // A bare FID the record does not have is a real #N/A (golden batch17).
        if (o.bareFid && isAbsent(o.sources[0].slice(RT.length))) {
          row.push(NA_ERROR);
          continue;
        }
        const sources = o.ifAbsent && isAbsent(o.ifAbsent.fid) ? o.ifAbsent.sources : o.sources;
        const vals = sources.map((s) => at(r, s));
        const v = vals.find((x) => !empty(x));
        // Evidence fills empty cells only: an error entry is keyed by (RIC,
        // field), not by row, so with several rows (SDate/EDate, Frq) it must
        // not wipe out the periods that do have a value.
        const err =
          empty(v) && cells.error && isDataGridOutput(o) ? cells.error(ric, o, outputHeaders(res.columns, idx, o)) : null;
        const rt = err === null && empty(v) && cells.realtime && !isDataGridOutput(o) ? cells.realtime(ric) : null;
        if (err !== null) row.push(err);
        // No tick yet is blank, not 0 [A]; an unknown RIC keeps its text.
        else if (rt !== null) row.push(o.kind === "tick" && typeof rt === "number" ? nul : rt);
        // DataGrid dates arrive as ISO strings; LSEG hands Excel date serials.
        else if (empty(v)) row.push(o.label === null && isDataGridOutput(o) ? nullCell(ric) : nul);
        else if (o.kind === "time") {
          const f = utcTimeToLocalFraction(v, cells.clock?.offsetMinutes, cells.clock?.now());
          row.push(typeof f === "number" ? new TimeSerial(f) : f);
        } else if (o.kind === "tick") row.push(` ${v}`);
        else row.push(dateCell(v));
      }
      body.push(row);
    }
  }

  let out = body;
  const excluded = !!display.excludeValues && display.rowHeaders.length > 0;
  if (excluded) {
    // EXCLUDEVA: the row-header columns alone; under CH they are headed as
    // columns of their own, not as an empty corner (golden opt_tr_excludeva).
    out = body.map((row) => row.slice(0, display.rowHeaders.length));
    // Goldens opt_tr_excludeva ("Instrument"), w_tr_excludeva_ch_date ("Date").
    if (display.columnHeaders) out = [display.rowHeaders.map((h) => (h === "instrument" ? "Instrument" : "Date")), ...out];
  } else if (display.columnHeaders) {
    // With row headers too, the cells above them are the table's empty
    // corner (goldens tr_multi_ric_field_chfd_rhin, lay_dest_ch_fd_rh_in).
    const head: any[] = display.rowHeaders.map(() => "");
    plan.outputs.forEach((o, i) => head.push(labels ? labels[i] : o.header ?? (o.label || o.sources[0])));
    out = [head, ...body];
  }
  if (out.length === 0) out = [[nul]];
  out = pad(out);
  return display.transpose ? transpose(out) : out;
}

// --- RHistory ---------------------------------------------------------------

/** One requested RHistory output column. */
/** One requested RHistory output column; a field column takes the first non-empty of `fids`. */
export type HistoryColumn =
  /**
   * `datetime`: tick data and intraday bars, date-time cells; `localTime`: the
   * hub's UTC instants shown in the PC's time zone (intraday bars and ticks);
   * `shiftMinutes`: added to each instant first (intraday bars: one interval,
   * see intradayMinutes).
   */
  | { kind: "timestamp"; label: string; datetime?: boolean; localTime?: boolean; shiftMinutes?: number }
  | { kind: "field"; fids: string[]; label: string };

// Legacy `<FID>.<property>` → get_history FID. The FID defaults to TRDPRC_1
// (`.Close` = `TRDPRC_1.Close`).
const PROP_FIDS: Record<string, Record<string, string>> = {
  TRDPRC_1: { CLOSE: "TRDPRC_1", VALUE: "TRDPRC_1", OPEN: "OPEN_PRC", HIGH: "HIGH_1", LOW: "LOW_1" },
};
// The current LSEG add-in rejects `BID.Close`-style prefixes with "Invalid
// field(s)." (golden rh_bid_ask_prefix), so EAV does too.
const REJECTED_BASES = new Set(["BID", "ASK"]);
const ANY_FID_PROPS: Record<string, string> = { VOLUME: "ACVOL_UNS", COUNT: "NUM_MOVES" };
const LEGACY_PROPS = new Set(["TIMESTAMP", "CLOSE", "VALUE", "OPEN", "HIGH", "LOW", "VOLUME", "COUNT"]);

function legacyProp(field: string): string | null {
  const i = field.lastIndexOf(".");
  if (i < 0) return null;
  const p = field.slice(i + 1).toUpperCase();
  return LEGACY_PROPS.has(p) ? p : null;
}

/** True for a bar property field (`.Open`, `.High`, `.Low`, `.Close`), which tick data does not have. */
export function isBarField(field: string): boolean {
  const p = legacyProp(field);
  return p === "OPEN" || p === "HIGH" || p === "LOW" || p === "CLOSE";
}

/** True when any field uses the legacy `.Property` syntax (marks a Refinitiv-style call). */
export function hasLegacyHistoryFields(fields: string[]): boolean {
  return fields.some((f) => legacyProp(f) !== null);
}

/**
 * Map one RHistory field to an output column. Throws on combos with no known
 * FID. `intraday`: intraday bars or ticks, where `.Close` stays the last trade.
 */
export function mapHistoryField(field: string, intraday = false): HistoryColumn {
  const prop = legacyProp(field);
  if (!prop) return { kind: "field", fids: [field], label: field };
  const i = field.lastIndexOf(".");
  const base = (field.slice(0, i) || "TRDPRC_1").toUpperCase();
  if (REJECTED_BASES.has(base)) throw new Error(`Unsupported RHistory field "${field}".`);
  // CH:Fd headers: Eikon labels the default-FID properties "Trade Open",
  // "Trade High", "Trade Low", "Trade Close", "Trade Volume".
  const trade: Record<string, string> = { OPEN: "Trade Open", HIGH: "Trade High", LOW: "Trade Low", CLOSE: "Trade Close", VOLUME: "Trade Volume" };
  const label = i === 0 ? trade[prop] || field.slice(1) : field;
  if (prop === "TIMESTAMP") return { kind: "timestamp", label };
  if (ANY_FID_PROPS[prop]) return { kind: "field", fids: [ANY_FID_PROPS[prop]], label };
  const fid = PROP_FIDS[base] ? PROP_FIDS[base][prop] : prop === "CLOSE" || prop === "VALUE" ? base : undefined;
  if (!fid) throw new Error(`Unsupported RHistory field "${field}".`);
  // Interday `.Close` is the official close, OFF_CLOSE (golden y_rh_vod_default:
  // VOD.L 10/16 is 122.05, where TRDPRC_1 is 120.79; spec §6.2), else, day by
  // day, the last trade TRDPRC_1 (a null OFF_CLOSE, or a RIC without one; see
  // rhistoryLaid). Intraday bars keep the last trade first. Some venues (Xetra equities,
  // e.g. SAPG.DE) carry no TRDPRC_1 history, so it falls back to OFF_CLOSE.
  if (fid !== "TRDPRC_1") return { kind: "field", fids: [fid], label };
  const officialFirst = !intraday && i === 0 && prop === "CLOSE";
  return { kind: "field", fids: officialFirst ? ["OFF_CLOSE", "TRDPRC_1"] : ["TRDPRC_1", "OFF_CLOSE"], label };
}

// LSEG interval spellings the interface doesn't know: 1MO = monthly, 1Q =
// quarterly (get_history's 1M / 3M), and the intraday bars, where LSEG's 1M
// is one MINUTE: get_history's own 1M is monthly, so those go as 1min, 5min...
// (golden x_rh_interval_*). Each value is mapped once, so 1M -> 1min is never
// read again as the hub's monthly 1M.
const INTRADAY_ALIASES: Record<string, string> = { "1M": "1min", "5M": "5min", "10M": "10min", "30M": "30min", "60M": "60min" };
const INTERVAL_ALIASES: Record<string, string> = { "1MO": "1M", "1Q": "3M", ...INTRADAY_ALIASES };
const HISTORY_DATE_KEYS = new Set(["START", "END"]);

/** True for an unsigned relative value other than 0D (5D, 2W): ignored as a START, an error as an END. */
const isFutureRelative = (value: string): boolean =>
  UNSIGNED_RELATIVE_RE.test(value.trim()) && !ZERO_RELATIVE_RE.test(value.trim());

/**
 * Normalize an RHistory request string: serials → ISO dates, Refinitiv interval
 * names mapped. An unsigned non-zero START (5D, 2W) is dropped: LSEG ignores
 * it, giving the full history up to END (goldens x_rh_start_5d, x_rh_s5d_yearly,
 * x_rh_s2w_yearly).
 */
export function normalizeHistoryRequest(raw: string): string {
  const kept = parseKv(raw, ":").filter((p) => !(p.key.toUpperCase() === "START" && isFutureRelative(p.value)));
  const pairs = kept.map((p) => {
    const k = p.key.toUpperCase();
    if (HISTORY_DATE_KEYS.has(k)) return { key: p.key, value: normalizeDate(p.value) };
    const alias = k === "INTERVAL" ? INTERVAL_ALIASES[p.value.toUpperCase()] : undefined;
    return alias ? { key: p.key, value: alias } : p;
  });
  return kvToString(pairs, ":");
}

// The request keys LSEG's RHistory knows; any other is "<KEY> is not a valid
// request parameter." (goldens opt_rh_bad_request_key, o_rh_day: DAY too).
const HISTORY_REQUEST_KEYS = new Set(["START", "END", "INTERVAL", "NBROWS", "ADJUSTED", "SOURCE", "EVENT", "TRADETIME", "TIMEZONE"]);
const TICK_INTERVALS = new Set(["TICK", "TAS", "TAQ"]);
// The intervals LSEG's RHistory was seen to take, in any case (goldens
// rh_interval_1mo, opt_rh_interval_1w, v_rh_interval_1y / 1q, w_rh_interval_1d,
// w_rh_tick_value_nbrows, v_rh_interval_tas, w_rh_interval_taq, and the
// intraday x_rh_interval_1m / 5m / 10m / 30m / 60m). Everything else captured
// is "Invalid value for INTERVAL.": 2M, 3M, 15M, 120M, 1MIN, 3MO, 6MO, 12MO,
// 7D, D, WEEKLY, 1H, HOUR, 60MIN, 7X.
const HISTORY_INTERVALS = new Set(["1D", "1W", "1MO", "1Q", "1Y", "TICK", "TAS", "TAQ", ...Object.keys(INTRADAY_ALIASES)]);
// A signed relative START or END (-3D, -1D, +3D) is "Invalid value for
// START/END." (goldens o_rh_end_relative, v_rh_end_relative, x_rh_start_plus3d,
// x_rh_end_plus3d, x_rh_sminus5d_eminus1d, x_rh_s0d_eminus1d); END:0D is today
// (golden w_rh_end_0d). An unsigned one other than 0D is an error as END
// (END:5D, goldens x_rh_end_plus5d / x_rh_e5d_early_start) and ignored as START.
const SIGNED_RELATIVE_RE = /^[-+]\d+[DWMQY]$/i;
const UNSIGNED_RELATIVE_RE = /^\d+[DWMQY]$/i;
const ZERO_RELATIVE_RE = /^0+[DWMQY]$/i;
// ADJUSTED values LSEG takes, in any case (goldens o_rh_adjusted_yes / _no,
// v_rh_adjusted_y, w_rh_adjusted_n / _lower); 0, 1 and TRUE are rejected.
const ADJUSTED_VALUES = new Set(["YES", "NO", "Y", "N"]);

/** True for a tick INTERVAL (TICK, TAS, TAQ): no bars, so no .Open/.High/.Low/.Close. */
export function isTickInterval(request: string): boolean {
  const i = parseKv(request, ":").find((p) => p.key.toUpperCase() === "INTERVAL");
  return !!i && TICK_INTERVALS.has(i.value.trim().toUpperCase());
}

/**
 * LSEG's text for the first RHistory request option it rejects, or null.
 * Accepted as LSEG does and sent on as typed: ADJUSTED:YES|NO|Y, EVENT,
 * TRADETIME, TIMEZONE, NBROWS (goldens o_rh_adjusted_yes / _no, v_rh_adjusted_y,
 * o_rh_event, o_rh_tradetime, opt_rh_timezone, rh_nbrows). Rejected: unknown
 * keys, other ADJUSTED values, a signed relative START or END (START is
 * reported before END), any SOURCE (XPAR, IDN) and any interval outside
 * HISTORY_INTERVALS. Once every option passes, LSEG's request validation error
 * is an unsigned relative END other than 0D, and a START later than the END
 * (0D read as `today`, the PC's calendar date; goldens x_rh_start_0d_dated_end,
 * x_rh_sdated_after_edated). START = END is a valid single day.
 */
export function historyRequestError(raw: string, today: Date = new Date()): string | null {
  const pairs = parseKv(raw, ":");
  const value = (key: string) => pairs.find((p) => p.key.toUpperCase() === key)?.value.trim();
  const signed = (v: string | undefined) => v !== undefined && SIGNED_RELATIVE_RE.test(v);
  for (const p of pairs) {
    const k = p.key.toUpperCase();
    if (!HISTORY_REQUEST_KEYS.has(k)) return invalidRequestParam(p.key);
    if ((k === "START" || k === "END") && signed(p.value.trim())) {
      return invalidValueFor(k === "END" && signed(value("START")) ? "START" : k);
    }
    const bad =
      (k === "INTERVAL" && !HISTORY_INTERVALS.has(p.value.trim().toUpperCase())) ||
      (k === "ADJUSTED" && !ADJUSTED_VALUES.has(p.value.trim().toUpperCase())) ||
      k === "SOURCE";
    if (bad) return invalidValueFor(k);
  }
  const end = value("END");
  if (end !== undefined && isFutureRelative(end)) return LSEG_TEXT.REQUEST_VALIDATION;
  const from = dayOf(value("START"), today);
  const to = dayOf(end, today);
  return from && to && from > to ? LSEG_TEXT.REQUEST_VALIDATION : null;
}

/**
 * LSEG's text for an RHistory Refresh argument (the 4th) it rejects, or null.
 * Empty and `FRQ:1D` are accepted, the series unchanged; `1` and `Y` are
 * "<word> is not a valid refresh parameter." (goldens rh_refresh_abs_frq /
 * _empty / _1 / _y). [A] beyond those: FRQ with any non-empty value is
 * accepted (refusing a formula LSEG may take costs more than taking one it
 * refuses); the first rejected word is named (another key, or FRQ with no
 * value, by its key, as a bad request key is). EAV still refreshes nothing
 * on its own.
 */
export function historyRefreshError(raw: string): string | null {
  // Its words: `KEY:value` (blanks allowed around the colon) or a bare word.
  const re = /(\w+)\s*:\s*(\S*)|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) {
    const [, key, value, bare] = m;
    if (bare !== undefined) return invalidRefreshParam(bare);
    if (key.toUpperCase() !== "FRQ" || !value) return invalidRefreshParam(key);
  }
  return null;
}

/** A local calendar date as YYYY-MM-DD. */
const localIso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * The calendar day (YYYY-MM-DD) of a START/END value: 0D is `today`, a serial
 * or ISO date its own day; anything else (absent, relative, odd) has none.
 */
function dayOf(value: string | undefined, today: Date): string | null {
  if (value === undefined) return null;
  if (/^0D$/i.test(value)) return localIso(today);
  const m = /^\d{4}-\d{2}-\d{2}/.exec(normalizeDate(value));
  return m ? m[0] : null;
}

/** True for the intraday bar intervals (1M, 5M, 10M, 30M, 60M: minutes), whose timestamps carry a time of day. */
export function isIntradayInterval(request: string): boolean {
  return intradayMinutes(request) > 0;
}

/**
 * The length in minutes of an intraday bar INTERVAL (1M → 1 … 60M → 60), 0 for
 * any other. The hub labels a bar by its start, Workspace by its end, so EAV
 * adds one interval to each bar's timestamp (spec §6.2; golden
 * x_rh_interval_1m_nbrows: the hub's 15:37Z bar reads 17:38 on a Paris PC).
 */
export function intradayMinutes(request: string): number {
  const i = parseKv(request, ":").find((p) => p.key.toUpperCase() === "INTERVAL");
  const token = i ? i.value.trim().toUpperCase() : "";
  return token in INTRADAY_ALIASES ? Number(token.slice(0, -1)) : 0;
}

// Daily bars: the only interval whose window LSEG get_history reads
// differently from Refinitiv (see widenHistoryWindow). Weekly, monthly,
// quarterly and yearly bars are dated at the period's end, past END (goldens
// opt_rh_interval_1w: 10/2 for END:2026-10-01; rh_interval_1mo: 10/31;
// v_rh_interval_1q / 1y: 12/31/2026), so they are not trimmed.
const DAILY = new Set(["", "1D"]);
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export interface HistoryWindow {
  /** Request string actually sent (window widened by a day each side). */
  request: string;
  /** Inclusive date bounds to trim the result to; absent → no trimming. */
  from?: string;
  to?: string;
}

/**
 * Refinitiv treats START/END as inclusive (`START:d END:d` = the bar of day
 * d); LSEG get_history does not — a same-day window comes back empty and
 * the start day is dropped. For daily requests with absolute dates (END:0D
 * read as today's), ask
 * for one extra day on each side and trim back to [START, END] afterwards.
 * Other intervals, relative dates and NBROWS-limited requests are sent
 * unchanged.
 */
export function widenHistoryWindow(normalized: string, today: Date = new Date()): HistoryWindow {
  // END:0D is today, and as inclusive as a dated END (golden w_rh_end_0d: the
  // START day is kept); START:0D is today too, so START:0D END:0D is the one
  // day of a single-day window (golden x_rh_start_0d).
  const pairs = parseKv(normalized, ":").map((p) =>
    ["START", "END"].includes(p.key.toUpperCase()) && /^0D$/i.test(p.value.trim()) ? { key: p.key, value: localIso(today) } : p
  );
  normalized = kvToString(pairs, ":");
  const get = (k: string) => pairs.find((p) => p.key.toUpperCase() === k);
  const start = get("START");
  const end = get("END");
  const interval = (get("INTERVAL")?.value || "").toUpperCase();
  if (!start || !end || get("NBROWS") || !DAILY.has(interval)) return { request: normalized };
  if (!ISO_DAY_RE.test(start.value) || !ISO_DAY_RE.test(end.value)) return { request: normalized };
  const from = start.value;
  const to = end.value;
  const widened = pairs.map((p) =>
    p === start ? { key: p.key, value: addDays(from, -1) } : p === end ? { key: p.key, value: addDays(to, 1) } : p
  );
  return { request: kvToString(widened, ":"), from, to };
}

/** Keep only rows whose index date (YYYY-MM-DD part) is within [from, to]. */
export function trimHistory(t: TableResult, from?: string, to?: string): TableResult {
  if (!from || !to || !t.index) return t;
  const data: any[][] = [];
  const index: any[] = [];
  t.index.forEach((ts, i) => {
    const day = String(ts || "").slice(0, 10);
    if (day >= from && day <= to) {
      data.push(t.data[i]);
      index.push(ts);
    }
  });
  return { ...t, columns: t.columns, data, index };
}

export interface HistoryDisplay {
  columnHeaders: boolean;
  /** CH includes In: multi-RIC blocks get their RIC as a header row. */
  instrumentHeaders?: boolean;
  ascending: boolean;
  /** TSREPEAT (default YES): every RIC block keeps its timestamp column; NO: the first one only. */
  tsRepeat: boolean;
  /** TRANSPOSE:YES: the whole table turned, dates across. */
  transpose: boolean;
  /** NULL:NA: a gap is a real #N/A error; NULL:SKIP: a row with a gap is dropped. */
  nullMode?: "NA" | "SKIP";
  /** LSEG's text for the first display option it rejects; the call shows it alone. */
  error?: string;
}

// The display keys LSEG's RHistory knows. Any other is "<KEY> is not a valid
// display parameter." (golden rh_two_rics_close: `RH:Timestamp`).
const HISTORY_DISPLAY_KEYS = new Set(["SORT", "TSREPEAT", "CH", "NULL", "TRANSPOSE"]);

/**
 * Parse the RHistory display string ("SORT:ASC CH:Fd TSREPEAT:NO"). Default
 * sort is newest-first (SORT:DESC, golden opt_rh_sort_desc_explicit).
 * NULL takes NA (a gap is a real #N/A, golden w_rh_null_na_blanks) or SKIP
 * (a row with a gap is dropped, golden w_rh_null_skip_blanks); x and BLANK
 * are "Invalid value for NULL." (goldens o_rh_null_display, v_rh_null_blank).
 */
export function parseHistoryDisplay(raw: string): HistoryDisplay {
  const d: HistoryDisplay = { columnHeaders: false, ascending: false, tsRepeat: true, transpose: false };
  for (const p of parseKv(raw, ":")) {
    const k = p.key.toUpperCase();
    if (!HISTORY_DISPLAY_KEYS.has(k)) d.error = d.error || invalidDisplayParam(p.key);
    else if (k === "NULL") {
      const v = p.value.trim().toUpperCase();
      if (v === "NA" || v === "SKIP") d.nullMode = v;
      else d.error = d.error || invalidValueFor(k);
    }
    else if (k === "CH") {
      d.columnHeaders = true;
      if (p.value.split(/[;,]/).some((x) => /^IN$/i.test(x.trim()))) d.instrumentHeaders = true;
    } else if (k === "SORT") d.ascending = /^A/i.test(p.value);
    // Captured: YES, Y, NO and N (goldens opt_rh_tsrepeat_yes / _no, w_rh_tsrepeat_y,
    // x_rh_tsrepeat_n).
    else if (k === "TSREPEAT") d.tsRepeat = !/^(NO|N)$/i.test(p.value.trim());
    // YES and Y turn the table (goldens opt_rh_transpose, w_rh_transpose_y);
    // TRUE and 1 are "Invalid value for TRANSPOSE." (goldens x_rh_transpose_true
    // / _1). As for ADJUSTED, only YES/Y/NO/N are taken.
    else if (/^(YES|Y)$/i.test(p.value.trim())) d.transpose = true;
    else if (!/^(NO|N)$/i.test(p.value.trim())) d.error = d.error || invalidValueFor(k);
  }
  return d;
}

/**
 * Lay out RHistory results Refinitiv-style. `columns` is the requested
 * layout (from mapHistoryField); with none requested, the timestamp plus
 * every returned column is shown. Rows run newest-first unless SORT:ASC.
 *
 * Several instruments sit side by side, one block of columns per input
 * instrument in input order, each with its own rows from the top (golden
 * rh_one_good_one_bad: BNPP.PA's closes in B, the bad RIC in C). An
 * instrument `failed` gives a text for shows it in its block's first cell; a
 * blank input, or one with no data and no error, leaves its block blank. With
 * CH, each block gets its field labels as a header row, and its RIC above
 * them when CH includes In. TSREPEAT:NO keeps one timestamp column (see
 * below); TRANSPOSE:YES turns the result. Returns [] when no instrument
 * produced data.
 */
export function layoutHistory(
  instruments: string[],
  byRic: (ric: string) => TableResult | null,
  columns: HistoryColumn[],
  display: HistoryDisplay,
  failed?: (ric: string) => string | null
): any[][] {
  return unmarkDates(layoutHistoryCells(instruments, byRic, columns, display, failed));
}

/** A table with one row and no values: what byRic returns for a RIC in layoutNoRows. */
const ONE_EMPTY_ROW: TableResult = { columns: [], data: [[]], index: [null] };

/**
 * The layout of a request LSEG answers with no rows at all (goldens x_rh_start_0d,
 * x_rh_start_0d_multi / _ch / _close_only, rh_valid_ric_no_rows): the usual
 * table (headers, one block per RIC) with ONE data row, every cell a real #N/A
 * error except the top-left one, the formula cell, which holds the text "#N/A"
 * (or, with CH, the header text that sits there).
 */
export function layoutNoRows(instruments: string[], columns: HistoryColumn[], display: HistoryDisplay): any[][] {
  return layoutHistoryCells(instruments, (ric) => (ric ? ONE_EMPTY_ROW : null), columns, display, undefined, true);
}

/** layoutHistory with its timestamp cells as DateSerial. */
export function layoutHistoryCells(
  instruments: string[],
  byRic: (ric: string) => TableResult | null,
  columns: HistoryColumn[],
  display: HistoryDisplay,
  failed?: (ric: string) => string | null,
  /** The tables are placeholders: each block gets one row of #N/A errors (see layoutNoRows). */
  noRows = false
): any[][] {
  const multi = instruments.filter(Boolean).length > 1;
  const columnsOf = (t: TableResult): HistoryColumn[] =>
    columns.length
      ? columns
      : [{ kind: "timestamp", label: "Timestamp" } as HistoryColumn].concat(
          t.columns.map((c) => ({ kind: "field", fids: [c], label: c } as HistoryColumn))
        );
  const rowsOf = (t: TableResult, cols: HistoryColumn[]): any[][] => {
    if (noRows) return [cols.map(() => NA_ERROR)];
    const idx = cols.map((c) =>
      c.kind === "field"
        ? c.fids.map((f) => t.columns.findIndex((x) => x.toUpperCase() === f.toUpperCase())).filter((i) => i >= 0)
        : []
    );
    const order = t.data.map((_, i) => i);
    if (!display.ascending) order.reverse();
    return order.map((i) => {
      const r = t.data[i];
      return cols.map((c, j) => {
        const v =
          c.kind === "timestamp"
            ? dateCell(t.index ? t.index[i] : "", !!c.datetime, !!c.localTime, c.shiftMinutes ?? 0)
            : idx[j].map((k) => r[k]).find((x) => x !== null && x !== undefined && x !== "");
        if (v !== null && v !== undefined) return v;
        return c.kind === "field" && display.nullMode === "NA" ? NA_ERROR : "";
      });
    });
  };
  // NULL:SKIP: a row with an empty field cell is dropped.
  const gap = (row: any[], cols: HistoryColumn[]) => cols.some((c, j) => c.kind === "field" && row[j] === "");
  const skipGaps = (rows: any[][], cols: HistoryColumn[]) =>
    display.nullMode === "SKIP" ? rows.filter((row) => !gap(row, cols)) : rows;

  // TRANSPOSE:YES turns the finished table (golden opt_rh_transpose). With no
  // rows, the formula cell shows the text, not the error.
  const finish = (g: any[][]) => {
    const out = display.transpose ? transpose(g) : g;
    if (noRows && out[0] && out[0][0] === NA_ERROR) out[0][0] = LSEG_TEXT.NA;
    return out;
  };

  if (!multi) {
    const body: any[][] = [];
    let head: string[] | null = null;
    for (const ric of instruments) {
      const t = ric ? byRic(ric) : null;
      if (!t || !t.data.length) continue;
      const cols = columnsOf(t);
      if (!head) head = cols.map((c) => c.label);
      body.push(...skipGaps(rowsOf(t, cols), cols));
    }
    if (!body.length) return [];
    return finish(pad(display.columnHeaders && head ? [head, ...body] : body));
  }

  // TSREPEAT:NO: the first block with data keeps its timestamp column, the
  // others drop theirs (golden opt_rh_tsrepeat_no). Their rows then have to
  // share its dates: every RIC is aligned on the union of all dates.
  const tables = instruments.map((ric) => (ric ? byRic(ric) : null));
  const shared = display.tsRepeat || noRows ? null : sharedIndex(tables);
  let tsShown = false;

  const blocks: { head: any[][]; rows: any[][]; width: number; cols: HistoryColumn[] | null }[] = [];
  let anyData = false;
  instruments.forEach((ric, i) => {
    const t = tables[i] && shared && tables[i]!.data.length ? alignTo(tables[i]!, shared) : tables[i];
    const has = !!t && t.data.length > 0;
    let cols = has ? columnsOf(t!) : columns;
    if (!display.tsRepeat) {
      if (has && !tsShown) tsShown = true;
      else cols = cols.filter((c) => c.kind !== "timestamp");
    }
    // A block of timestamps only, under TSREPEAT:NO, has nothing left to show.
    const width = has && !cols.length ? 0 : Math.max(1, cols.length);
    const head: any[][] = [];
    if (display.columnHeaders) {
      if (display.instrumentHeaders) head.push([ric, ...new Array(width - 1).fill("")]);
      head.push(cols.length ? cols.map((c) => c.label) : [""]);
    }
    let rows: any[][] = [];
    if (has) {
      anyData = true;
      // Aligned blocks drop their gap rows together, below.
      rows = shared ? rowsOf(t!, cols) : skipGaps(rowsOf(t!, cols), cols);
    } else {
      const err = ric && failed ? failed(ric) : null;
      if (err !== null) rows = [[err, ...new Array(width - 1).fill("")]];
    }
    blocks.push({ head, rows, width, cols: has ? cols : null });
  });
  if (!anyData) return [];
  if (shared && display.nullMode === "SKIP") {
    // Rows share their dates across blocks: a gap in any block drops the date.
    const drop = new Set<number>();
    for (const b of blocks) if (b.cols) b.rows.forEach((row, i) => gap(row, b.cols!) && drop.add(i));
    for (const b of blocks) if (b.cols) b.rows = b.rows.filter((_, i) => !drop.has(i));
  }
  const headHeight = blocks.reduce((a, b) => Math.max(a, b.head.length), 0);
  const height = headHeight + blocks.reduce((a, b) => Math.max(a, b.rows.length), 0);
  const out: any[][] = [];
  for (let r = 0; r < height; r++) {
    const line: any[] = [];
    for (const b of blocks) {
      const cells = r < headHeight ? b.head[r - (headHeight - b.head.length)] : b.rows[r - headHeight];
      for (let c = 0; c < b.width; c++) line.push(cells && c < cells.length ? cells[c] : "");
    }
    out.push(line);
  }
  return finish(out);
}

/** The dates of every table with rows, oldest first, each once. */
function sharedIndex(tables: (TableResult | null)[]): string[] {
  const dates = new Set<string>();
  for (const t of tables) if (t && t.data.length) (t.index || []).forEach((d) => dates.add(String(d)));
  return Array.from(dates).sort();
}

/** `t` with one row per date of `dates` (all-null where it has none). */
function alignTo(t: TableResult, dates: string[]): TableResult {
  const rows = new Map<string, any[]>();
  (t.index || []).forEach((d, i) => rows.set(String(d), t.data[i]));
  return { ...t, data: dates.map((d) => rows.get(d) || t.columns.map(() => null)), index: dates };
}

// --- Splitting batched responses ---------------------------------------------

const EMPTY: TableResult = { columns: [], data: [], index: [] };

/**
 * The key batched results are stored under: the RIC trimmed, case kept —
 * LSEG codes are case-sensitive (SIEGn.DE and SIEGN.DE are two instruments).
 */
export function ricKey(ric: string): string {
  return ric.trim();
}

/**
 * Look a RIC up exactly, else loosely when exactly one key matches: a sheet
 * may spell a RIC in another case than the answer, and the hub answers a
 * delayed instrument as `/VOD.L` when asked for `VOD.L`.
 */
export function lookupRic<T>(map: Map<string, T>, ric: string): T | undefined {
  const k = ricKey(ric);
  if (map.has(k)) return map.get(k);
  const loose = (x: string) => x.replace(/^\//, "").toUpperCase();
  const want = loose(k);
  const hits = Array.from(map.keys()).filter((x) => loose(x) === want);
  return hits.length === 1 ? map.get(hits[0]) : undefined;
}

/**
 * Hand each instrument's `unresolved` / `errors` entries of a multi-RIC
 * response to its own slice (exact key, else the one loose match, else a new
 * empty slice made by `blank`), so every per-RIC sub-result knows about its
 * own RIC only.
 */
function attachEntries(t: TableResult, out: Map<string, TableResult>, blank: () => TableResult): void {
  const loose = (x: string) => x.replace(/^\//, "").toUpperCase();
  const slot = (instrument: string): TableResult => {
    const k = ricKey(String(instrument ?? ""));
    let s = out.get(k);
    if (!s) {
      const hits = Array.from(out.keys()).filter((x) => loose(x) === loose(k));
      if (hits.length === 1) s = out.get(hits[0]);
    }
    if (!s) {
      s = blank();
      out.set(k, s);
    }
    return s;
  };
  for (const u of t.unresolved || []) {
    const s = slot(u);
    s.unresolved = (s.unresolved || []).concat(u);
  }
  for (const e of t.errors || []) {
    const s = slot(e.instrument);
    s.errors = (s.errors || []).concat(e);
  }
  for (const a of t.absent || []) {
    const s = slot(a.instrument);
    s.absent = (s.absent || []).concat(a);
  }
  // Presence is evidence too: an `[]` on the response means the hub checked,
  // so every slice gets an (empty) array; an absent list stays absent.
  out.forEach((s) => {
    if (t.unresolved && !s.unresolved) s.unresolved = [];
    if (t.errors && !s.errors) s.errors = [];
  });
}

/** Carry a response's `unresolved` / `errors` over to a reshaped copy of it. */
function withEntries(from: TableResult, to: TableResult): TableResult {
  if (from.unresolved) to.unresolved = from.unresolved;
  if (from.errors) to.errors = from.errors;
  if (from.absent) to.absent = from.absent;
  return to;
}

/**
 * Split a multi-instrument DataGrid response by its Instrument column.
 * Each slice drops that column; instruments with no rows are absent.
 */
function instrumentColumn(t: TableResult): number {
  const col = t.columns.findIndex((c) => String(c).toUpperCase() === "INSTRUMENT");
  return col < 0 ? 0 : col;
}

export function splitByInstrument(t: TableResult): Map<string, TableResult> {
  const col = instrumentColumn(t);
  const columns = t.columns.filter((_, i) => i !== col);
  const out = new Map<string, TableResult>();
  for (const r of t.data) {
    const k = ricKey(String(r[col] ?? ""));
    if (!out.has(k)) out.set(k, { columns, data: [] });
    out.get(k)!.data.push(r.filter((_, i) => i !== col));
  }
  attachEntries(t, out, () => ({ columns, data: [] }));
  return out;
}

/** A single-instrument DataGrid response with its Instrument column removed (all rows kept). */
export function dropInstrumentColumn(t: TableResult): TableResult {
  const col = instrumentColumn(t);
  return withEntries(t, {
    columns: t.columns.filter((_, i) => i !== col),
    data: t.data.map((r) => r.filter((_, i) => i !== col)),
  });
}

const DATE_COLUMN_RE = /^(DATE|TIMESTAMP|INDEX)$/i;
const ISO_DATE_VALUE_RE = /^\d{4}-\d{2}-\d{2}/;

/**
 * The value column of a history table asked with the hub's default fields
 * (no FID named), or -1. Only numeric columns count (date and text columns
 * aside): the only one, else FIXING_1, then BID, then the first ([A]: only
 * the one-column case is captured, EURIBOR1YD= gives FIXING_1 alone).
 */
export function defaultValueColumn(t: TableResult): number {
  const isDate = (j: number) =>
    DATE_COLUMN_RE.test(String(t.columns[j])) ||
    (t.data.some((r) => r[j] !== null && r[j] !== undefined) &&
      t.data.every((r) => r[j] === null || r[j] === undefined || (typeof r[j] === "string" && ISO_DATE_VALUE_RE.test(r[j]))));
  const numeric = t.columns
    .map((_, j) => j)
    .filter((j) => !isDate(j) && t.data.some((r) => typeof r[j] === "number"));
  if (numeric.length <= 1) return numeric.length ? numeric[0] : -1;
  for (const name of ["FIXING_1", "BID"]) {
    const j = numeric.find((x) => String(t.columns[x]).toUpperCase() === name);
    if (j !== undefined) return j;
  }
  return numeric[0];
}

/**
 * Two get_history tables of one RIC side by side, rows matched on their
 * timestamp (`index`): the columns of `b` that `a` lacks are added, and a row
 * only one of them has gets nulls in the other's columns. Rows stay in
 * timestamp order. Used to put TRDPRC_1 next to OFF_CLOSE (asked apart), so
 * a fallback column reads the first non-empty per row (spec §6.2).
 */
export function mergeHistory(a: TableResult | null, b: TableResult | null): TableResult | null {
  if (!a || !a.index) return a ?? b;
  if (!b || !b.index) return a;
  const up = (c: any) => String(c).toUpperCase();
  const added = b.columns.map((c, j) => ({ c, j })).filter(({ c }) => !a.columns.some((x) => up(x) === up(c)));
  const rows = new Map<string, any[]>();
  const stamps = new Map<string, any>();
  a.data.forEach((r, i) => {
    const k = String(a.index![i]);
    rows.set(k, [...r, ...added.map(() => null)]);
    stamps.set(k, a.index![i]);
  });
  let grew = false;
  b.data.forEach((r, i) => {
    const k = String(b.index![i]);
    let row = rows.get(k);
    if (!row) {
      row = [...a.columns.map(() => null), ...added.map(() => null)];
      rows.set(k, row);
      stamps.set(k, b.index![i]);
      grew = true;
    }
    added.forEach(({ j }, n) => (row![a.columns.length + n] = r[j]));
  });
  const keys = Array.from(rows.keys());
  // Both come from the same hub, timestamps in one ISO format: text order is time order.
  if (grew) keys.sort();
  return {
    columns: [...a.columns, ...added.map(({ c }) => c)],
    data: keys.map((k) => rows.get(k)!),
    index: keys.map((k) => stamps.get(k)),
  };
}

/**
 * Split a multi-instrument get_history response. LSEG names columns `<RIC>`
 * when one field was requested and `<RIC>.<FIELD>` otherwise; RICs contain
 * dots themselves, so each column goes to the longest RIC it starts with.
 * Rows that are all-null for an instrument (artifacts of the shared date
 * index) are dropped from its slice.
 */
export function splitHistory(t: TableResult, rics: string[], fids: string[]): Map<string, TableResult> {
  const keys = Array.from(new Set(rics.map(ricKey))).sort((a, b) => b.length - a.length);
  const owns = (col: string, r: string) => col === r || col.startsWith(r + ".");
  const cols = new Map<string, { idx: number; name: string }[]>();
  t.columns.forEach((c, idx) => {
    const col = String(c);
    // Exact case first; a case-insensitive match only when nothing matches exactly.
    const k =
      keys.find((r) => owns(col, r)) || keys.find((r) => owns(col.toUpperCase(), r.toUpperCase()));
    if (!k) return;
    const name = col.length === k.length ? fids[0] || col : col.slice(k.length + 1);
    if (!cols.has(k)) cols.set(k, []);
    cols.get(k)!.push({ idx, name });
  });
  const out = new Map<string, TableResult>();
  cols.forEach((cs, k) => {
    const data: any[][] = [];
    const index: any[] = [];
    t.data.forEach((r, i) => {
      const row = cs.map((c) => r[c.idx]);
      if (row.every((v) => v === null || v === undefined || v === "")) return;
      data.push(row);
      index.push(t.index ? t.index[i] : null);
    });
    out.set(k, { columns: cs.map((c) => c.name), data, index });
  });
  attachEntries(t, out, () => ({ columns: [], data: [], index: [] }));
  return out;
}

export { EMPTY as EMPTY_TABLE };
